/**
 * 生成结果编辑器（P2-T2）：把文学剧本 / 分镜表的结构化产物渲染成可编辑表单。
 *
 * 只负责「展示 + 产出新 result」，写回 store 由调用方（编辑器）负责。所有编辑走
 * model/generation 的不可变纯函数，组件本身无副作用。字段用底色区分、不加边框，
 * 呼应面板「只留最外一条细线」的观感。
 */
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';

import {
  addShot,
  isLiteraryScriptResult,
  isStoryboardResult,
  moveShot,
  removeShot,
  updateDialogue,
  updateScene,
  updateScriptMeta,
  updateShot,
  type GeneratedPayload,
  type LiteraryScript,
  type Storyboard,
} from '../../model/generation';

const field =
  'w-full rounded-control bg-surface-base px-2 py-1 text-uiSm text-ink-primary outline-none focus:bg-surface-raised';
const card = 'flex flex-col gap-1 rounded-control bg-surface-base/40 p-2';
const iconBtn = 'p-0.5 text-ink-muted transition-colors hover:text-ink-primary disabled:opacity-30';

interface GenResultEditorProps {
  result: GeneratedPayload;
  onChange: (r: GeneratedPayload) => void;
}

function ScriptEditor({ script, onChange }: { script: LiteraryScript; onChange: (s: LiteraryScript) => void }) {
  return (
    <div className="flex flex-col gap-2">
      <input className={field} value={script.title} onChange={(e) => onChange(updateScriptMeta(script, { title: e.target.value }))} />
      <textarea
        className={`${field} min-h-14 resize-none`}
        value={script.logline}
        onChange={(e) => onChange(updateScriptMeta(script, { logline: e.target.value }))}
      />
      {script.episodes.map((ep) => (
        <div key={ep.id} className="flex flex-col gap-2">
          <div className="text-uiSm text-ink-faint">{ep.title}</div>
          {ep.scenes.map((sc) => (
            <div key={sc.id} className={card}>
              <input className={field} value={sc.heading} onChange={(e) => onChange(updateScene(script, sc.id, { heading: e.target.value }))} />
              <textarea
                className={`${field} min-h-12 resize-none`}
                value={sc.action}
                onChange={(e) => onChange(updateScene(script, sc.id, { action: e.target.value }))}
              />
              {sc.dialogues.map((d, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="shrink-0 text-uiSm text-ink-muted">{d.character}</span>
                  <input className={field} value={d.line} onChange={(e) => onChange(updateDialogue(script, sc.id, i, e.target.value))} />
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** 带“参数名：”前缀的小字段（紧凑规格表用） */
function ShotField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex min-w-0 items-baseline gap-1 text-uiSm">
      <span className="shrink-0 text-ink-faint">{label}：</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 rounded-control bg-surface-base px-1.5 py-0.5 text-ink-primary outline-none focus:bg-surface-raised"
      />
    </label>
  );
}

function ShotEditor({ sb, onChange }: { sb: Storyboard; onChange: (s: Storyboard) => void }) {
  return (
    <div className="flex flex-col gap-2">
      <input className={field} value={sb.title} onChange={(e) => onChange({ ...sb, title: e.target.value })} />
      {sb.shots.map((sh) => (
        <div key={sh.id} className={card}>
          <div className="flex items-center justify-between">
            <span className="font-num text-uiSm font-semibold text-accent">#{sh.index}</span>
            <div className="flex items-center gap-1">
              <button type="button" className={iconBtn} title="上移" disabled={sh.index === 1} onClick={() => onChange(moveShot(sb, sh.id, -1))}>
                <ArrowUp size={13} />
              </button>
              <button type="button" className={iconBtn} title="下移" disabled={sh.index === sb.shots.length} onClick={() => onChange(moveShot(sb, sh.id, 1))}>
                <ArrowDown size={13} />
              </button>
              <button type="button" className={iconBtn} title="删除该镜" disabled={sb.shots.length <= 1} onClick={() => onChange(removeShot(sb, sh.id))}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
          {/* 场景 + 画面动作：长文本，整行 */}
          <ShotField label="场景" value={sh.scene} onChange={(v) => onChange(updateShot(sb, sh.id, { scene: v }))} />
          <label className="flex flex-col gap-0.5 text-uiSm">
            <span className="text-ink-faint">画面动作：</span>
            <textarea className={`${field} min-h-12 resize-none`} value={sh.action} onChange={(e) => onChange(updateShot(sb, sh.id, { action: e.target.value }))} />
          </label>
          {/* 镜头语言参数：一行三个 */}
          <div className="grid grid-cols-3 gap-x-2 gap-y-1">
            <ShotField label="角色" value={sh.characters.join('、')} onChange={(v) => onChange(updateShot(sb, sh.id, { characters: v.split(/[、,，]/).map((s) => s.trim()).filter(Boolean) }))} />
            <ShotField label="道具" value={sh.props.join('、')} onChange={(v) => onChange(updateShot(sb, sh.id, { props: v.split(/[、,，]/).map((s) => s.trim()).filter(Boolean) }))} />
            <ShotField label="景别" value={sh.shotSize} onChange={(v) => onChange(updateShot(sb, sh.id, { shotSize: v }))} />
            <ShotField label="机位" value={sh.cameraAngle} onChange={(v) => onChange(updateShot(sb, sh.id, { cameraAngle: v }))} />
            <ShotField label="运镜" value={sh.cameraMove} onChange={(v) => onChange(updateShot(sb, sh.id, { cameraMove: v }))} />
            <ShotField label="光影" value={sh.lighting} onChange={(v) => onChange(updateShot(sb, sh.id, { lighting: v }))} />
            <ShotField label="色彩" value={sh.colorTone} onChange={(v) => onChange(updateShot(sb, sh.id, { colorTone: v }))} />
            <ShotField label="情绪" value={sh.emotion} onChange={(v) => onChange(updateShot(sb, sh.id, { emotion: v }))} />
            <ShotField label="转场" value={sh.transition} onChange={(v) => onChange(updateShot(sb, sh.id, { transition: v }))} />
            <label className="flex items-baseline gap-1 text-uiSm">
              <span className="shrink-0 text-ink-faint">时长：</span>
              <input
                type="number"
                min={0}
                value={sh.durationSec}
                onChange={(e) => onChange(updateShot(sb, sh.id, { durationSec: Number(e.target.value) || 0 }))}
                className="w-11 rounded-control bg-surface-base px-1.5 py-0.5 text-ink-primary outline-none focus:bg-surface-raised"
              />
              <span className="text-ink-faint">秒</span>
            </label>
          </div>
          {/* 台词：变量，整行 */}
          <ShotField label="台词" value={sh.dialogue} onChange={(v) => onChange(updateShot(sb, sh.id, { dialogue: v }))} />
          {/* 完备性补充（旧版 8 层所需）：场景描述/造型视觉/音效/音色 */}
          <div className="grid grid-cols-2 gap-x-2 gap-y-1">
            {([['场景描述', sh.scene_description, (v: string) => updateShot(sb, sh.id, { scene_description: v })], ['造型视觉', sh.character_visual, (v: string) => updateShot(sb, sh.id, { character_visual: v })], ['音效/BGM', sh.sound_design, (v: string) => updateShot(sb, sh.id, { sound_design: v })], ['台词音色', sh.voice_direction, (v: string) => updateShot(sb, sh.id, { voice_direction: v })]] as const).map(([label, value, set]) => (
              <label key={label} className="flex flex-col gap-0.5 text-uiSm">
                <span className="text-ink-faint">{label}：</span>
                <textarea className={`${field} min-h-10 resize-none`} value={value} onChange={(e) => onChange(set(e.target.value))} />
              </label>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn-ghost inline-flex self-start items-center gap-1 px-2 py-1 text-uiSm"
        onClick={() => onChange(addShot(sb, `sh${Date.now().toString(36)}`))}
      >
        <Plus size={13} />
        加镜
      </button>
    </div>
  );
}

export function GenResultEditor({ result, onChange }: GenResultEditorProps) {
  if (isLiteraryScriptResult(result)) {
    return <ScriptEditor script={result.script} onChange={(s) => onChange({ type: 'literaryScript', script: s })} />;
  }
  if (isStoryboardResult(result)) {
    return <ShotEditor sb={result.storyboard} onChange={(s) => onChange({ type: 'storyboard', storyboard: s })} />;
  }
  return null;
}
